/**
 * Dernière place de l'avatar, retenue dans le navigateur (par compte et par
 * campus) : en revenant dans le campus moins d'une heure après l'avoir quitté,
 * on reprend là où on était, sinon arrivée habituelle (bureau, puis carte
 * d'arrivée). Écrite par ZoneScene, lue une fois par BootScene à l'entrée.
 * localStorage peut être indisponible (navigation privée) : tout est best-effort.
 */
import { useGameStore } from '@campus/core';

export interface LastPlace { zoneSlug: string; x: number; y: number }

/** Durée pendant laquelle on reprend sa dernière place (décision 2026-10-29). */
export const LAST_PLACE_TTL_MS = 60 * 60 * 1000;

function storageKey(): string | null {
  const jwt = useGameStore.getState().jwt;
  if (!jwt) return null;
  try {
    const part = jwt.split('.')[1] ?? '';
    const payload = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/'))) as { sub?: string; tid?: string };
    if (!payload.sub || payload.sub.startsWith('guest:')) return null; // invités : zéro persistance
    return `placio:lastPlace:${payload.tid ?? ''}:${payload.sub}`;
  } catch {
    return null;
  }
}

export function saveLastPlace(place: LastPlace): void {
  const key = storageKey();
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify({ ...place, at: Date.now() }));
  } catch { /* stockage indisponible */ }
}

/** Dernière place si quittée depuis moins de LAST_PLACE_TTL_MS, sinon null. */
export function readRecentLastPlace(): LastPlace | null {
  const key = storageKey();
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<LastPlace & { at: number }>;
    if (typeof v.zoneSlug !== 'string' || typeof v.x !== 'number' || typeof v.y !== 'number' || typeof v.at !== 'number') return null;
    if (Date.now() - v.at > LAST_PLACE_TTL_MS) return null;
    return { zoneSlug: v.zoneSlug, x: v.x, y: v.y };
  } catch {
    return null;
  }
}
